import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectAgreementsData } from '../../store/general/general-slice-selectors';
import { isHttpError } from '@common/interfaces/api/http-error';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import SelectInput from '../forms/SelectInput';
import useValidatedSelectEffect from '../../hooks/useValidatedSelectEffect';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import { resetAgreementsRestState } from '../../store/general/general-slice';
import { uploadAgreementFile } from '../../store/general/general-actions';
import LinearProgressWithLabel from '../misc/LinearProgressWithLabel';
import { Language } from '../../models/enums/languages';

const UploadFileForm: React.FC<{ open: boolean; agreementId: string; onClose: () => void }> = ({
  open,
  agreementId,
  onClose,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const agreementsData = useAppSelector((state) => selectAgreementsData(state));

  const { resetToggle, updateResetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: agreementsData.rest.postUploadFileError,
      resetPostHandler: () =>
        dispatch(
          resetAgreementsRestState({
            postUploadFileStatus: HttpFetchStatus.Idle,
            postUploadFileError: null,
          }),
        ),
    });

  useEffect(() => {
    if (agreementsData.rest.postUploadFileStatus === HttpFetchStatus.Success && open) {
      updateResetToggle();
      onClose();
    }
  }, [agreementsData.rest.postUploadFileStatus, onClose, open, updateResetToggle]);

  const [file, setFile] = useState<File | null>(null);

  const {
    value: language,
    validationResult: languageValidationResult,
    valueChangeHandler: languageChangeHandler,
  } = useValidatedSelectEffect(
    Language.ENGLISH,
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('language'),
    removeServerError,
    resetToggle,
  );

  const closeHandler = () => {
    onClose();
    dispatch(
      resetAgreementsRestState({
        postUploadFileStatus: HttpFetchStatus.Idle,
        postUploadFileError: null,
      }),
    );
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (file != null) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', language as Language);
      formData.append('agreementId', agreementId);
      dispatch(uploadAgreementFile(formData));
      setFile(null);
    }
  };

  const uploadFileChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
    }
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={closeHandler} maxWidth="sm" fullWidth>
        <form onSubmit={formSubmitHandler}>
          <DialogTitle textAlign="center" variant="h2">
            {t('title-upload-file')}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={2}>
              {agreementsData.rest.postUploadFileError !== null &&
                isHttpError(agreementsData.rest.postUploadFileError) && (
                  <CustomAlert
                    severity="error"
                    header={t(agreementsData.rest.postUploadFileError.message)}
                  />
                )}
              <label htmlFor="file_input">
                <input
                  hidden
                  type="file"
                  id="file_input"
                  name="file_input"
                  onChange={uploadFileChangeHandler}
                />
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                  <Button
                    variant="outlined"
                    component="span"
                    size="large"
                    sx={{ whiteSpace: 'nowrap' }}
                    data-testid="button-upload-file-id"
                  >
                    {t('button-upload-file')}
                  </Button>
                  <Typography>
                    {file == null ? t('info-upload-no-file-selected') : file.name.split('.')[0]}
                  </Typography>
                </Stack>
              </label>
              <SelectInput
                label={t('input-label-file-language')}
                value={language}
                onChange={languageChangeHandler}
                validationResult={languageValidationResult}
              >
                {Object.values(Language).map((c) => (
                  <MenuItem key={c} value={c}>
                    {t('language-' + c)}
                  </MenuItem>
                ))}
              </SelectInput>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={1} width="100%" justifyContent="flex-end">
              {agreementsData.uploadProgress != null && (
                <LinearProgressWithLabel
                  label={t('info-uploading-file')}
                  value={agreementsData.uploadProgress}
                />
              )}
              <Button onClick={closeHandler}>{t('button-cancel')}</Button>
              <Button
                variant="contained"
                data-testid="button-add"
                disabled={
                  agreementsData.rest.postUploadFileStatus === HttpFetchStatus.Loading ||
                  file == null
                }
                type="submit"
              >
                {agreementsData.rest.postUploadFileStatus === HttpFetchStatus.Loading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('button-upload-file')
                )}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
      <CustomSnackbar
        open={agreementsData.rest.postUploadFileStatus === HttpFetchStatus.Success}
        title={t('success-uploading-file')}
        onClose={closeHandler}
      />
    </React.Fragment>
  );
};

export default UploadFileForm;
